import { api } from '../../api'
import type {
  CurrenciesResponse,
  CurrencyFilters,
  DataIngestionLogsFilters,
  DataIngestionLogsGraphFilters,
  GlobalSawSettings,
  GlobalSettings,
  IntervalChartFilters,
  IntervalStatisticsFilters,
  NtdFieldsResponse,
  NtdFieldValue,
  NtdMailingTemplateValue,
  NtdType,
  NtdValueByIdParams,
  NtdValueParams,
  PlatformAvgDuration,
  PlatformHealthOverview,
  ProductTypeFilters,
  ProductTypesResponse,
  Proxy,
  ProxyFilters,
  CreateProxyPayload,
  RequestFilters,
  RequestsResponse,
  UpdateGlobalSawSettingsPayload,
  UpdateGlobalSettingsPayload,
  WhitelistSettings,
  EbayTokenRaw,
} from '../../../types/system.types'

export type {
  Currency,
  CurrenciesResponse,
  CurrencyFilters,
  DataIngestionLog,
  DataIngestionLogsFilters,
  DataIngestionLogsGraphFilters,
  EbayToken,
  EbayTokenRaw,
  GlobalSawSettings,
  GlobalSettings,
  IntervalChartFilters,
  IntervalStatisticsFilters,
  NtdFieldsResponse,
  NtdFieldValue,
  NtdMailingTemplateValue,
  NtdType,
  NtdValueByIdParams,
  NtdValueParams,
  PlatformAvgDuration,
  PlatformHealthOverview,
  ProductType,
  ProductTypeFilters,
  ProductTypesResponse,
  Proxy,
  ProxyFilters,
  ProxyStatus,
  CreateProxyPayload,
  Request,
  RequestFilters,
  RequestsResponse,
  CreateRequestPayload,
  UpdateRequestPayload,
  UpdateGlobalSawSettingsPayload,
  UpdateGlobalSettingsPayload,
  WhitelistSettings,
} from '../../../types/system.types'

// ─── Currencies ──────────────────────────────────────────────────────────────

export const getCurrencies = async (params: CurrencyFilters): Promise<CurrenciesResponse> => {
  const res = await api.get<CurrenciesResponse>('/settings/currencies', {
    params: {
      page_size: params.page_size ?? 1000,
      search: params.search,
    },
  })
  return res.data
}

// ─── Requests ────────────────────────────────────────────────────────────────

export const getRequests = async (params: RequestFilters): Promise<RequestsResponse> => {
  const res = await api.get<RequestsResponse>('/requests/', { params })
  return res.data
}

export const putRequest = async ({ id, data }: { id: string; data: { importance: number; status: string } }): Promise<void> => {
  await api.put(`/requests/${id}`, data)
}

export const postRequest = async (data: { name: string; importance: number; type: string }): Promise<void> => {
  await api.post('/requests/', data)
}

// ─── Global Settings ─────────────────────────────────────────────────────────

export const getGlobalSettings = async (): Promise<GlobalSettings> => {
  const res = await api.get<GlobalSettings>('/settings/global')
  return res.data
}

export const editGlobalSettings = async (data: UpdateGlobalSettingsPayload): Promise<GlobalSettings> => {
  const res = await api.put<GlobalSettings>('/settings/global', data)
  return res.data
}

// ─── Whitelist ───────────────────────────────────────────────────────────────

export const getWhitelist = async (): Promise<WhitelistSettings> => {
  const res = await api.get<WhitelistSettings>('settings/whitelisted')
  return res.data
}

export const editWhitelist = async (data: WhitelistSettings): Promise<WhitelistSettings> => {
  const res = await api.put<WhitelistSettings>('settings/whitelisted', data)
  return res.data
}

// ─── NTD Fields ──────────────────────────────────────────────────────────────

export const getNtdFields = async (type: NtdType): Promise<NtdFieldsResponse> => {
  const res = await api.get<NtdFieldsResponse>(`/settings/ntd/${type}/fields`)
  return res.data
}

export const getDefaultNtdValue = async (params: NtdValueParams): Promise<NtdFieldValue> => {
  const res = await api.get<NtdFieldValue>(`/settings/ntd/${params.type}`, { params })
  return res.data
}

export const editDefaultNtdValue = async (data: { type: NtdType; [key: string]: string }): Promise<NtdFieldValue> => {
  const res = await api.put<NtdFieldValue>(`/settings/ntd/${data.type}/`, data)
  return res.data
}

export const getNtdValue = async (params: NtdValueByIdParams): Promise<NtdFieldValue> => {
  const res = await api.get<NtdFieldValue>(`/settings/ntd/${params.type}/${params.id}`, { params })
  return res.data
}

export const editNtdValue = async (data: { [key: string]: string; id: string; type: NtdType }): Promise<NtdFieldValue> => {
  const res = await api.put<NtdFieldValue>(`/settings/ntd/${data.type}/${data.id}`, data)
  return res.data
}

export const exchangeEbayCode = async (data: { code: string; member_id?: string; brand_id?: string }): Promise<void> => {
  await api.post('/settings/platform_auth/ebay/exchange_code/', data)
}

// ─── NTD Mailing Templates ───────────────────────────────────────────────────

export const getDefaultNtdMailingTemplate = async (params: { field_name: string }): Promise<NtdMailingTemplateValue> => {
  const res = await api.get<NtdMailingTemplateValue>('/settings/ntd/mailing', { params })
  return res.data
}

export const editDefaultNtdMailingTemplate = async (data: { field_name: string; field_value: string }): Promise<NtdMailingTemplateValue> => {
  const res = await api.put<NtdMailingTemplateValue>('/settings/ntd/mailing', data)
  return res.data
}

export const getPlatformNtdMailingtemplate = async (platform_id: string, params: { field_name: string }): Promise<NtdMailingTemplateValue> => {
  const res = await api.get<NtdMailingTemplateValue>(`/settings/ntd/mailing/${platform_id}`, { params })
  return res.data
}

export const editPlatformNtdMailingTemplate = async (
  platform_id: string,
  data: { field_name: string; field_value: string }
): Promise<NtdMailingTemplateValue> => {
  const res = await api.put<NtdMailingTemplateValue>(`/settings/ntd/mailing/${platform_id}`, data)
  return res.data
}

export const getSAWNtdMailingTemplate = async (params: { field_name: string; phase: string }): Promise<NtdMailingTemplateValue> => {
  const res = await api.get<NtdMailingTemplateValue>(`/settings/ntd/mailing/saw/${params.phase}/`, { params })
  return res.data
}

export const editSAWNtdMailingTemplate = async (phase: string, data: { field_name: string; field_value: string }): Promise<NtdMailingTemplateValue> => {
  const res = await api.put<NtdMailingTemplateValue>(`/settings/ntd/mailing/saw/${phase}/`, data)
  return res.data
}

export const getMemberSAWNtdMailingTemplate = async (params: {
  field_name: string
  phase: string
  member_id: string
}): Promise<NtdMailingTemplateValue> => {
  const res = await api.get<NtdMailingTemplateValue>(`/settings/ntd/mailing/saw/${params.phase}/member/${params.member_id}/`, { params })
  return res.data
}

export const editMemberSAWNtdMailingTemplate = async (
  phase: string,
  member_id: string,
  data: { field_name: string; field_value: string }
): Promise<NtdMailingTemplateValue> => {
  const res = await api.put<NtdMailingTemplateValue>(`/settings/ntd/mailing/saw/${phase}/member/${member_id}/`, data)
  return res.data
}

export const createMemberSAWNtdMailingTemplate = async (phase: string, member_id: string): Promise<void> => {
  await api.post(`/settings/ntd/mailing/saw/${phase}/member/${member_id}/`)
}

export const deleteMemberSAWNtdMailingTemplate = async (phase: string, member_id: string): Promise<void> => {
  await api.delete(`/settings/ntd/mailing/saw/${phase}/member/${member_id}/`)
}

export const getBrandSAWNtdMailingTemplate = async (params: {
  field_name: string
  phase: string
  brand_id: string
}): Promise<NtdMailingTemplateValue> => {
  const res = await api.get<NtdMailingTemplateValue>(`/settings/ntd/mailing/saw/${params.phase}/brand/${params.brand_id}/`, { params })
  return res.data
}

export const editBrandSAWNtdMailingTemplate = async (
  phase: string,
  brand_id: string,
  data: { field_name: string; field_value: string }
): Promise<NtdMailingTemplateValue> => {
  const res = await api.put<NtdMailingTemplateValue>(`/settings/ntd/mailing/saw/${phase}/brand/${brand_id}/`, data)
  return res.data
}

export const createBrandSAWNtdMailingTemplate = async (phase: string, brand_id: string): Promise<void> => {
  await api.post(`/settings/ntd/mailing/saw/${phase}/brand/${brand_id}/`)
}

export const deleteBrandSAWNtdMailingTemplate = async (phase: string, brand_id: string): Promise<void> => {
  await api.delete(`/settings/ntd/mailing/saw/${phase}/brand/${brand_id}/`)
}

// ─── Global SAW Settings ─────────────────────────────────────────────────────

export const getGlobalSawSettings = async (): Promise<GlobalSawSettings> => {
  const res = await api.get<GlobalSawSettings>('/settings/global/saw')
  return res.data
}

export const editGlobalSawSettings = async (data: UpdateGlobalSawSettingsPayload): Promise<GlobalSawSettings> => {
  const res = await api.put<GlobalSawSettings>('/settings/global/saw', data)
  return res.data
}

// ─── Product Types ───────────────────────────────────────────────────────────

export const getProductTypes = async (params: ProductTypeFilters): Promise<ProductTypesResponse> => {
  const res = await api.get<ProductTypesResponse>('/settings/product_types', { params })
  return res.data
}

// ─── eBay Tokens ─────────────────────────────────────────────────────────────

export const getEbayMemberTokens = async (): Promise<EbayTokenRaw[]> => {
  const res = await api.get<EbayTokenRaw[] | { data: EbayTokenRaw[] }>('/settings/ebay/member_tokens/')
  return Array.isArray(res.data) ? res.data : res.data.data || []
}

export const getEbayBrandTokens = async (): Promise<EbayTokenRaw[]> => {
  const res = await api.get<EbayTokenRaw[] | { data: EbayTokenRaw[] }>('/settings/ebay/brand_tokens/')
  return Array.isArray(res.data) ? res.data : res.data.data || []
}

// ─── Proxies ─────────────────────────────────────────────────────────────────

export const getProxies = async (params: ProxyFilters): Promise<{ data: Proxy[]; totalPages: number }> => {
  const res = await api.get<{ data: Proxy[]; totalPages: number }>('/crawlers/proxy', { params })
  return res.data
}

export const deleteProxy = async (id: string): Promise<void> => {
  await api.delete(`/crawlers/proxy/${id}`)
}

export const bulkCreateProxies = async (data: CreateProxyPayload[]): Promise<void> => {
  await api.post('/crawlers/proxy/', data)
}

export const bulkDeleteProxies = async (): Promise<void> => {
  await api.delete('/crawlers/proxy/delete_all/')
}

// ─── Data Ingestion Logs ─────────────────────────────────────────────────────

export const getDataIngestionLogs = async (params: DataIngestionLogsFilters) => {
  const res = await api.get('/logs/list', {
    params: {
      offset: params.offset,
      search: params.search,
      level: params.logLevel === '' ? undefined : params.logLevel,
      min_date: `${params.startDate}:00`,
      max_date: `${params.endDate}:00`,
      page_size: params.pageSize,
    },
  })
  return res.data
}

export const getDataIngestionLogsGraph = async (params: DataIngestionLogsGraphFilters) => {
  const res = await api.get('/logs/graph', {
    params: {
      min_date: `${params.startDate}:00`,
      max_date: `${params.endDate}:00`,
    },
  })
  return res.data
}

// ─── Platform Health ─────────────────────────────────────────────────────────

export const getPlatformHealth = async (): Promise<PlatformHealthOverview> => {
  const res = await api.get<PlatformHealthOverview>('/platforms/healthOverview')
  return res.data
}

export const checkPlatformHealth = async (): Promise<void> => {
  await api.post('/crawlers/healthcheck/')
}

// ─── Dataprovider Auth ───────────────────────────────────────────────────────

export const getDataproviderAuthStatus = async (): Promise<{ authenticated: boolean }> => {
  const res = await api.get<{ authenticated: boolean }>('/settings/dataprovider/auth/')
  return res.data
}

export const loginDataprovider = async (data: { username: string; password: string }): Promise<void> => {
  await api.post('/settings/dataprovider/auth/', data)
}

// ─── Interval Statistics ─────────────────────────────────────────────────────

export const getIntervalStatistics = async (params: IntervalStatisticsFilters) => {
  const res = await api.get('/crawlers/daily', { params })
  return res.data
}

export const getIntervalChart = async (params: IntervalChartFilters): Promise<PlatformAvgDuration[]> => {
  const res = await api.get<PlatformAvgDuration[]>('/crawlers/platforms/avg_duration', { params })
  return res.data
}
